import React, { useRef, useMemo } from 'react';
import styled from 'styled-components';
import Icon from '../Icon';
import theme from '../theme';

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid ${({ $color }) => $color || theme.colors.NEU_300};
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: ${({ $color }) => $color || theme.colors.NEU_500};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $color }) => $color || theme.colors.NEU_900};
    color: ${({ $color }) => $color || theme.colors.NEU_900};
    background: ${({ $color }) => ($color ? `${$color}15` : theme.colors.NEU_100)};
  }

  &.swiper-button-disabled,
  &:disabled {
    opacity: 0.3;
    cursor: default;
    pointer-events: none;
  }
`;

const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &:has(> :disabled:first-child):has(> :disabled:last-child),
  &:has(> .swiper-button-disabled:first-child):has(> .swiper-button-disabled:last-child) {
    display: none;
  }
`;

/**
 * Stable component — define outside the hook so React never unmounts it on re-render.
 */
export const CarouselNavButtons = ({ prevRef, nextRef, color, onPrev, onNext, disablePrev, disableNext }) => (
  <NavWrapper>
    <NavButton ref={prevRef} $color={color} onClick={onPrev} disabled={disablePrev} aria-label='Previous'>
      <Icon.ArrowLeft size='small' />
    </NavButton>
    <NavButton ref={nextRef} $color={color} onClick={onNext} disabled={disableNext} aria-label='Next'>
      <Icon.ArrowRight size='small' />
    </NavButton>
  </NavWrapper>
);

/**
 * Hook that provides external navigation controls for any Swiper instance.
 *
 * Usage:
 *   const { navProps, swiperNavProps } = useCarouselNav();
 *
 *   <CarouselNavButtons {...navProps} />                // neutral
 *   <CarouselNavButtons {...navProps} color='#E91E63' /> // branded
 *
 *   <Swiper {...swiperNavProps} modules={[Navigation, ...]}>
 *     ...
 *   </Swiper>
 */
const useCarouselNav = () => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const navProps = useMemo(() => ({ prevRef, nextRef }), []);

  const swiperNavProps = {
    navigation: {
      prevEl: prevRef.current,
      nextEl: nextRef.current,
    },
    onBeforeInit: (swiper) => {
      if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
        swiper.params.navigation.prevEl = prevRef.current;
        swiper.params.navigation.nextEl = nextRef.current;
      }
    },
    onInit: (swiper) => {
      if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
        swiper.params.navigation.prevEl = prevRef.current;
        swiper.params.navigation.nextEl = nextRef.current;
        swiper.navigation.init();
        swiper.navigation.update();
      }
    },
  };

  return { navProps, swiperNavProps };
};

export default useCarouselNav;
